import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useConsumerAuth } from '../../context/ConsumerAuthContext';
import axios from 'axios';
import { 
  Building2, CheckCircle, Clock, AlertCircle, XCircle, 
  ArrowRight, ArrowLeft, Send, DollarSign, Percent, Calendar,
  RefreshCw, Check
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LenderSubmission = () => {
  const navigate = useNavigate();
  const { applicationId } = useParams();
  const [searchParams] = useSearchParams();
  const { token, loading: authLoading } = useConsumerAuth();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [application, setApplication] = useState(null);
  const [eligibleLenders, setEligibleLenders] = useState([]);
  const [selectedLenders, setSelectedLenders] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [offers, setOffers] = useState([]);
  const [step, setStep] = useState('select'); // 'select', 'submitting', 'results'
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading && !token) {
      navigate('/beth-finance/login');
      return;
    }
    if (token && applicationId) {
      fetchData();
    }
  }, [token, applicationId, authLoading]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Get application details
      const appRes = await axios.get(`${API}/consumer/applications/${applicationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplication(appRes.data);

      // Get eligible lenders
      const lendersRes = await axios.get(`${API}/lenders/eligible/${applicationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEligibleLenders(lendersRes.data.eligible_lenders || []);
      
      // Pre-select all lenders
      setSelectedLenders(lendersRes.data.eligible_lenders?.map(l => l.lender_id) || []);

      // Check if already submitted
      const subsRes = await axios.get(`${API}/lenders/submissions/${applicationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (subsRes.data.submissions?.length > 0) {
        setSubmissions(subsRes.data.submissions);
        setStep('results');
        
        // Get offers
        const offersRes = await axios.get(`${API}/lenders/offers/${applicationId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOffers(offersRes.data.offers || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleLender = (lenderId) => {
    setSelectedLenders(prev => 
      prev.includes(lenderId) 
        ? prev.filter(id => id !== lenderId)
        : [...prev, lenderId]
    );
  };

  const selectAll = () => {
    setSelectedLenders(eligibleLenders.map(l => l.lender_id));
  };

  const deselectAll = () => {
    setSelectedLenders([]);
  };

  const handleSubmit = async () => {
    if (selectedLenders.length === 0) {
      setError('Please select at least one lender');
      return;
    }

    setSubmitting(true);
    setStep('submitting');
    setError('');

    try {
      const response = await axios.post(
        `${API}/lenders/submit/${applicationId}`,
        { lender_ids: selectedLenders },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSubmissions(response.data.submissions || []);
      
      // Fetch offers
      const offersRes = await axios.get(`${API}/lenders/offers/${applicationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOffers(offersRes.data.offers || []);
      
      setStep('results');
    } catch (err) {
      setError(err.response?.data?.detail || 'Submission failed');
      setStep('select');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptOffer = async (offerId) => {
    try {
      await axios.post(
        `${API}/lenders/offers/${offerId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh offers
      const offersRes = await axios.get(`${API}/lenders/offers/${applicationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOffers(offersRes.data.offers || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to accept offer');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'pending': return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'declined': return <XCircle className="w-5 h-5 text-red-500" />;
      case 'submitted': return <Send className="w-5 h-5 text-blue-500" />;
      default: return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'declined': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'submitted': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'accepted': return 'bg-green-500/20 text-green-400 border-green-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#012169] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading lender information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#012169] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/beth-finance/dashboard" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-[#E31837] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xl">B</span>
              </div>
              <span className="text-xl font-bold tracking-tight">BETH Finance</span>
            </Link>
            <button 
              onClick={() => navigate('/beth-finance/dashboard')}
              className="flex items-center text-sm hover:text-[#E31837] transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Application Summary */}
        {application && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-[#012169]">Submit to Lenders</h1>
                <p className="text-gray-500">Application: {applicationId}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-[#012169]">{formatCurrency(application.loan_amount)}</p>
                <p className="text-sm text-gray-500 capitalize">{application.loan_type} Loan</p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
            <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Select Lenders Step */}
        {step === 'select' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-[#012169]">Select Lenders</h2>
                  <p className="text-gray-500 text-sm">Choose which lenders to submit your application to</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={selectAll} className="text-sm text-[#012169] hover:underline">Select All</button>
                  <span className="text-gray-300">|</span>
                  <button onClick={deselectAll} className="text-sm text-gray-500 hover:underline">Deselect All</button>
                </div>
              </div>
            </div>

            <div className="divide-y divide-gray-100">
              {eligibleLenders.map((lender) => (
                <label 
                  key={lender.lender_id}
                  className="flex items-center p-4 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedLenders.includes(lender.lender_id)}
                    onChange={() => toggleLender(lender.lender_id)}
                    className="w-5 h-5 rounded border-gray-300 text-[#012169] focus:ring-[#012169]"
                  />
                  <div className="ml-4 flex-1">
                    <div className="flex items-center">
                      <Building2 className="w-5 h-5 text-[#012169] mr-2" />
                      <span className="font-medium text-gray-900">{lender.name}</span>
                    </div>
                    <div className="flex gap-4 mt-1 text-sm text-gray-500">
                      <span>APR: {lender.min_apr}% - {lender.max_apr}%</span>
                      <span>Max: {formatCurrency(lender.max_loan_amount)}</span>
                      <span>Min Credit: {lender.min_credit_score}</span>
                    </div>
                  </div>
                </label>
              ))}

              {eligibleLenders.length === 0 && (
                <div className="p-8 text-center">
                  <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No eligible lenders found for this loan amount and type.</p>
                </div>
              )}
            </div>

            {eligibleLenders.length > 0 && (
              <div className="p-6 border-t border-gray-200">
                <button
                  onClick={handleSubmit}
                  disabled={selectedLenders.length === 0}
                  className="w-full bg-[#E31837] hover:bg-[#c4142f] disabled:bg-gray-300 text-white py-3 rounded-lg font-semibold transition-all flex items-center justify-center"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Submit to {selectedLenders.length} Lender{selectedLenders.length !== 1 ? 's' : ''}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Submitting Step */}
        {step === 'submitting' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#012169] mx-auto mb-6"></div>
            <h2 className="text-xl font-bold text-[#012169] mb-2">Submitting Your Application</h2>
            <p className="text-gray-500">Please wait while we submit to {selectedLenders.length} lenders...</p>
          </div>
        )}

        {/* Results Step */}
        {step === 'results' && (
          <>
            {/* Submissions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-[#012169]">Lender Responses</h2>
                  <button onClick={fetchData} className="text-[#012169] hover:text-[#01337d]">
                    <RefreshCw className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="divide-y divide-gray-100">
                {submissions.map((sub, idx) => (
                  <div key={idx} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(sub.status)}
                      <div>
                        <p className="font-medium text-gray-900">{sub.lender_name}</p>
                        <p className="text-sm text-gray-500">{sub.message}</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs border ${getStatusColor(sub.status)}`}>
                      {sub.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Loan Offers */}
            {offers.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-lg font-bold text-[#012169]">Loan Offers</h2>
                  <p className="text-gray-500 text-sm">Compare and accept the best offer for you</p>
                </div>
                
                <div className="divide-y divide-gray-100">
                  {offers.map((offer, idx) => (
                    <div key={idx} className="p-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Building2 className="w-5 h-5 text-[#012169]" />
                            <span className="font-bold text-gray-900">{offer.lender_name}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs border ${getStatusColor(offer.status)}`}>
                              {offer.status}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-3 gap-6 mt-4">
                            <div>
                              <div className="flex items-center text-gray-500 text-sm mb-1">
                                <Percent className="w-4 h-4 mr-1" /> APR
                              </div>
                              <p className="text-xl font-bold text-[#012169]">{offer.interest_rate}%</p>
                            </div>
                            <div>
                              <div className="flex items-center text-gray-500 text-sm mb-1">
                                <DollarSign className="w-4 h-4 mr-1" /> Monthly
                              </div>
                              <p className="text-xl font-bold text-green-600">{formatCurrency(offer.monthly_payment)}</p>
                            </div>
                            <div>
                              <div className="flex items-center text-gray-500 text-sm mb-1">
                                <Calendar className="w-4 h-4 mr-1" /> Term
                              </div>
                              <p className="text-xl font-bold text-gray-900">{offer.loan_term_months} mo</p>
                            </div>
                          </div>
                        </div>
                        
                        {offer.status === 'pending' && (
                          <button
                            onClick={() => handleAcceptOffer(offer.offer_id)}
                            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium flex items-center"
                          >
                            <Check className="w-4 h-4 mr-2" /> Accept
                          </button>
                        )}
                        
                        {offer.status === 'accepted' && (
                          <div className="bg-green-100 text-green-700 px-4 py-2 rounded-lg flex items-center">
                            <CheckCircle className="w-5 h-5 mr-2" />
                            Accepted
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Next Steps */}
            <div className="mt-6 text-center">
              <Link
                to="/beth-finance/dashboard"
                className="inline-flex items-center text-[#012169] hover:underline"
              >
                Return to Dashboard <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default LenderSubmission;
